<template>
  <div class="matrix-editor-wrap">
    <main class="page">

      <p class="breadcrumb">
        SEQ / Admin /
        <router-link to="/admin/questions" class="breadcrumb-link">Questions</router-link>
        / {{ breadcrumbTail }}
      </p>

      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">{{ pageTitle }}</h1>
          <p class="page-subtitle">
            Matrix: one question asked about many items, all answered on the same scale
          </p>
        </div>
        <div class="page-header-actions">
          <button
            type="button"
            class="btn btn-secondary"
            @click="onCancel"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!isValid || saving"
            @click="onSave"
          >{{ saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create question') }}</button>
        </div>
      </div>

      <p
        v-if="serverError"
        class="server-error"
        role="alert"
      >{{ serverError }}</p>

      <div
        v-if="isEdit && answerCount > 0"
        class="answer-warning"
        role="status"
      >
        <span class="warning-icon" aria-hidden="true">⚠️</span>
        This question has {{ answerCount }}
        {{ answerCount === 1 ? 'answer' : 'answers' }}.
        Editing items, scale, or scores may affect analysis.
      </div>

      <div v-if="loadError" class="load-error">
        <p>{{ loadError }}</p>
        <router-link to="/admin/questions" class="back-link">← Back</router-link>
      </div>

      <div v-else-if="loading" class="loading-state">
        Loading question…
      </div>

      <div v-else class="editor-grid">
        <!-- ───────── LEFT: form ───────── -->
        <section class="form-column">

          <!-- Fieldset 1: Identification -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Identification</legend>

            <div class="field">
              <label class="field-label" :for="`${uid}-code`">Code</label>
              <input
                :id="`${uid}-code`"
                v-model="form.code"
                type="text"
                class="field-input mono"
                :class="{ 'has-error': errors.code && touched.code }"
                placeholder="VEG_FREQ_MATRIX"
                autocomplete="off"
                spellcheck="false"
                @blur="touched.code = true"
                @input="onCodeInput"
              >
              <p class="field-hint">
                Short identifier, uppercase letters, digits, and underscores.
                Must be unique within your organization.
              </p>
              <p
                v-if="errors.code && touched.code"
                class="field-error"
              >{{ errors.code }}</p>
              <p
                v-if="isEdit && form.code !== initialSnapshot.code"
                class="field-hint dim"
              >Changing the code may affect analytics scripts that reference it.</p>
            </div>

            <div class="field">
              <label class="field-label">Section</label>
              <button
                type="button"
                class="section-pick-btn"
                :class="{ 'is-unassigned': form.section_id == null }"
                @click="showSectionPicker = true"
              >
                <span class="section-pick-label">{{ sectionPickLabel }}</span>
                <span class="section-pick-action">{{ form.section_id == null ? 'Pick section' : 'Change' }}</span>
              </button>
              <button
                v-if="form.section_id != null"
                type="button"
                class="section-clear-btn"
                @click="form.section_id = null"
              >Clear (Unassigned)</button>
            </div>

            <div class="field">
              <label class="field-label">Status</label>
              <div class="radio-row">
                <label class="radio-option">
                  <input type="radio" v-model="form.status" value="active">
                  <span>Active</span>
                </label>
                <label class="radio-option">
                  <input type="radio" v-model="form.status" value="archived">
                  <span>Archived</span>
                </label>
              </div>
            </div>
          </fieldset>

          <!-- Fieldset 2: Question content -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Question content</legend>

            <LocaleSelector
              v-model="activeLang"
              :locales="SUPPORTED_LOCALES"
              :labels="LOCALE_LABELS"
              :filled-fn="isLocaleFilled"
              hint="Fill in any or all languages"
              aria-label="Translation language"
            />

            <div class="field">
              <label class="field-label" :for="`${uid}-title-${activeLang}`">
                Question title <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
              </label>
              <textarea
                :id="`${uid}-title-${activeLang}`"
                v-model="form.content.title[activeLang]"
                class="field-input field-textarea"
                :class="{ 'has-error': errors.title && touched.title }"
                rows="2"
                :placeholder="placeholders.title[activeLang]"
                @blur="touched.title = true"
              ></textarea>
              <p class="field-hint">
                The shared question stem. Asked once, applies to every item below.
                Required in at least one language.
              </p>
              <p
                v-if="errors.title && touched.title"
                class="field-error"
              >{{ errors.title }}</p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-subtitle-${activeLang}`">
                Subtitle <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <input
                :id="`${uid}-subtitle-${activeLang}`"
                v-model="form.content.subtitle[activeLang]"
                type="text"
                class="field-input"
                :placeholder="placeholders.subtitle[activeLang]"
              >
              <p class="field-hint">
                Section label shown above the title, e.g. "Vegetables".
              </p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-prompt-${activeLang}`">
                Helper prompt <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <input
                :id="`${uid}-prompt-${activeLang}`"
                v-model="form.content.prompt[activeLang]"
                type="text"
                class="field-input"
                :placeholder="placeholders.prompt[activeLang]"
              >
              <p class="field-hint">
                Optional line below the title.
              </p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-help-${activeLang}`">
                Help text <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <textarea
                :id="`${uid}-help-${activeLang}`"
                v-model="form.help_text[activeLang]"
                class="field-input field-textarea"
                rows="3"
              ></textarea>
              <p class="field-hint">
                Optional explanation shown when the user taps a help icon.
                Leave empty in all languages to hide the icon.
              </p>
            </div>
          </fieldset>

          <!-- Fieldset 3: Answer scale -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">
              Answer scale
              <span class="option-count">
                {{ form.content.scale.options.length }} of 8 options
              </span>
            </legend>

            <p class="fieldset-note">
              This scale applies to every item below. Editing it changes
              the answer choices for every item simultaneously.
            </p>

            <p
              v-if="errors.options_count"
              class="field-error fieldset-error"
            >{{ errors.options_count }}</p>

            <VueDraggable
              v-model="form.content.scale.options"
              :animation="180"
              handle=".drag-handle"
              item-key="id"
              class="options-list"
            >
              <div
                v-for="(opt, idx) in form.content.scale.options"
                :key="opt.id"
                class="option-row"
              >
                <span
                  class="drag-handle"
                  aria-label="Drag to reorder"
                  title="Drag to reorder"
                >⋮⋮</span>

                <span class="option-number">{{ idx + 1 }}</span>

                <div class="option-fields">
                  <div class="option-label-cell">
                    <input
                      type="text"
                      class="field-input option-label-input"
                      :class="{ 'has-error': errors.option_labels[opt.id] }"
                      :placeholder="`Long label (${LOCALE_LABELS[activeLang]})`"
                      :value="opt.label[activeLang]"
                      @input="setOptionLabel(opt, activeLang, $event.target.value)"
                    >
                    <p class="field-hint tiny">Long form, for layouts with room (Vanilla A).</p>
                    <p
                      v-if="errors.option_labels[opt.id]"
                      class="field-error option-label-error"
                    >{{ errors.option_labels[opt.id] }}</p>
                  </div>

                  <div class="option-short-cell">
                    <input
                      type="text"
                      class="field-input option-short-input"
                      :class="{ 'has-warning': errors.option_shorts[opt.id] }"
                      :placeholder="`Short (${LOCALE_LABELS[activeLang]})`"
                      :value="opt.label_short[activeLang]"
                      @input="setOptionShort(opt, activeLang, $event.target.value)"
                    >
                    <p class="field-hint tiny">Compressed form for chips (Vanilla B).</p>
                    <p
                      v-if="errors.option_shorts[opt.id]"
                      class="field-warning option-label-warning"
                    >{{ errors.option_shorts[opt.id] }}</p>
                  </div>
                </div>

                <div class="option-score-cell">
                  <input
                    type="number"
                    step="1"
                    class="field-input number-input option-score-input"
                    v-model.number="opt.score"
                    :aria-label="`Score for option ${idx + 1}`"
                  >
                  <p class="field-hint tiny">Score</p>
                </div>

                <button
                  type="button"
                  class="option-delete"
                  :disabled="form.content.scale.options.length <= 2"
                  :title="form.content.scale.options.length <= 2
                    ? 'Minimum 2 options required'
                    : 'Delete option'"
                  aria-label="Delete option"
                  @click="requestDeleteOption(idx)"
                >×</button>
              </div>
            </VueDraggable>

            <p class="field-hint score-hint">
              Score is the same across all languages and may be negative.
            </p>

            <button
              type="button"
              class="btn btn-secondary add-option-btn"
              :disabled="form.content.scale.options.length >= 8"
              @click="addOption"
            >+ Add option</button>
          </fieldset>

          <!-- Fieldset 4: Items -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">
              Items
              <span class="option-count">
                {{ form.content.items.length }} of 50 items
              </span>
            </legend>

            <p
              v-if="errors.items_count"
              class="field-error fieldset-error"
            >{{ errors.items_count }}</p>

            <VueDraggable
              v-model="form.content.items"
              :animation="180"
              handle=".drag-handle"
              item-key="id"
              class="items-list"
            >
              <div
                v-for="(item, idx) in form.content.items"
                :key="item.id"
                class="item-row-wrap"
              >
                <div class="item-row">
                  <span
                    class="drag-handle"
                    aria-label="Drag to reorder"
                    title="Drag to reorder"
                  >⋮⋮</span>

                  <span class="option-number">{{ idx + 1 }}</span>

                  <div class="item-label-cell">
                    <input
                      type="text"
                      class="field-input item-label-input"
                      :class="{ 'has-error': errors.item_labels[item.id] }"
                      :placeholder="`Label (${LOCALE_LABELS[activeLang]})`"
                      :value="item.label[activeLang]"
                      @input="setItemLabel(item, activeLang, $event.target.value)"
                    >
                    <p
                      v-if="errors.item_labels[item.id]"
                      class="field-error option-label-error"
                    >{{ errors.item_labels[item.id] }}</p>
                  </div>

                  <button
                    type="button"
                    class="tooltip-toggle"
                    :class="{
                      'has-content': itemHasAnyTooltip(item),
                      'is-open': tooltipExpanded[item.id],
                    }"
                    :title="itemHasAnyTooltip(item)
                      ? 'Edit tooltip'
                      : 'Add tooltip'"
                    :aria-expanded="String(!!tooltipExpanded[item.id])"
                    @click="toggleTooltipEditor(item.id)"
                  >ⓘ</button>

                  <button
                    type="button"
                    class="option-delete"
                    :disabled="form.content.items.length <= 2"
                    :title="form.content.items.length <= 2
                      ? 'Minimum 2 items required'
                      : 'Delete item'"
                    aria-label="Delete item"
                    @click="requestDeleteItem(idx)"
                  >×</button>
                </div>

                <div
                  v-if="tooltipExpanded[item.id]"
                  class="item-tooltip-editor"
                >
                  <label class="field-label tooltip-label">
                    Tooltip body
                    <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                    <span class="optional">(optional)</span>
                  </label>
                  <textarea
                    :value="item.tooltip_body[activeLang]"
                    class="field-input field-textarea"
                    rows="2"
                    :placeholder="`Tooltip body for ${LOCALE_LABELS[activeLang]}`"
                    @input="setItemTooltip(item, activeLang, $event.target.value)"
                  ></textarea>
                  <p class="field-hint">
                    Shown when the user taps the ⓘ next to this item.
                    Leave empty in all languages to hide the icon.
                  </p>
                </div>
              </div>
            </VueDraggable>

            <div class="items-toolbar">
              <button
                type="button"
                class="btn btn-secondary add-option-btn"
                :disabled="form.content.items.length >= 50"
                @click="addItem"
              >+ Add item</button>
              <button
                type="button"
                class="btn btn-secondary"
                :disabled="form.content.items.length >= 50"
                @click="openPasteDialog"
              >Paste from list…</button>
            </div>
          </fieldset>

        </section>

        <!-- ───────── RIGHT: live preview ───────── -->
        <aside class="preview-column">
          <div class="preview-label">Live preview</div>
          <div class="preview-frame">
            <MatrixRenderer
              :title="form.content.title"
              :subtitle="form.content.subtitle"
              :prompt="form.content.prompt"
              :scale-options="form.content.scale.options"
              :items="form.content.items"
              :locale="activeLang"
              :preview="true"
            />
          </div>
          <p class="preview-note">
            Mock screen for the
            <strong>{{ LOCALE_LABELS[activeLang] }}</strong>
            translation rendered with the production Vanilla B layout.
            Switch tabs in the form to preview another language.
          </p>

          <RawJsonPanel
            :data="rawJsonView"
            title="Raw JSON (what gets saved)"
            hint="Updates live as you edit. Reflects the canonical save shape."
          />
        </aside>
      </div>

    </main>

    <!-- Paste-from-list dialog -->
    <div
      v-if="showPasteDialog"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paste-title"
      @click.self="closePasteDialog"
    >
      <div class="modal modal-paste">
        <h2 id="paste-title" class="modal-title">Paste item labels</h2>
        <p class="modal-text">
          One label per line. Each non-empty line becomes a new item with
          the label filled for the target language and empty for the others.
        </p>

        <div class="field">
          <label class="field-label" :for="`${uid}-paste-lang`">Target language</label>
          <select
            :id="`${uid}-paste-lang`"
            v-model="pasteLang"
            class="field-input field-select"
          >
            <option
              v-for="loc in SUPPORTED_LOCALES"
              :key="loc"
              :value="loc"
            >{{ LOCALE_LABELS[loc] }}</option>
          </select>
        </div>

        <div class="field">
          <label class="field-label" :for="`${uid}-paste-text`">Labels</label>
          <textarea
            :id="`${uid}-paste-text`"
            v-model="pasteText"
            class="field-input field-textarea"
            rows="10"
            :placeholder="`Tomatoes / tomato sauce\nCarrots\nOnions, leeks\n…`"
          ></textarea>
          <p class="field-hint">
            {{ pasteCount }} non-empty lines.
            {{ remainingSlots }} item slots remaining.
            {{ pasteCount > remainingSlots
              ? `Only the first ${remainingSlots} will be added.`
              : '' }}
          </p>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closePasteDialog"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="pasteCount === 0 || remainingSlots === 0"
            @click="onConfirmPaste"
          >Add {{ Math.min(pasteCount, remainingSlots) }} items</button>
        </div>
      </div>
    </div>

    <!-- Delete-row confirm dialog (options and items share this) -->
    <div
      v-if="pendingDelete"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-title"
      @click.self="cancelDelete"
    >
      <div class="modal modal-confirm modal-delete">
        <div class="modal-icon" aria-hidden="true">
          <span class="modal-icon-glyph">×</span>
        </div>
        <h2 id="delete-title" class="modal-title">{{ pendingDelete.title }}</h2>
        <p class="modal-text">
          <template v-if="pendingDelete.preview">
            <span class="modal-quote">“{{ pendingDelete.preview }}”</span>
            will be removed.
          </template>
          <template v-else>
            This {{ pendingDelete.kind === 'option' ? 'option' : 'item' }}
            is empty in every language. It will be removed.
          </template>
          This can't be undone, but no answers are affected until you save.
        </p>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="cancelDelete"
          >Keep it</button>
          <button
            type="button"
            class="btn btn-danger"
            @click="confirmDelete"
          >Delete {{ pendingDelete.kind }}</button>
        </div>
      </div>
    </div>

    <!-- Discard-changes confirm dialog -->
    <div
      v-if="showLeaveConfirm"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-title"
      @click.self="onCancelLeave"
    >
      <div class="modal modal-confirm">
        <h2 id="leave-title" class="modal-title">Discard changes?</h2>
        <p class="modal-text">Your unsaved changes will be lost.</p>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="onCancelLeave"
          >Keep editing</button>
          <button
            type="button"
            class="btn btn-danger"
            @click="onConfirmLeave"
          >Discard</button>
        </div>
      </div>
    </div>

    <!-- Section picker popup -->
    <SectionPickerPopup
      v-if="showSectionPicker"
      :current-section-id="form.section_id"
      @select-section="onSectionPicked"
      @close="showSectionPicker = false"
    />

    <!-- Toast -->
    <transition name="toast-fade">
      <div
        v-if="toast"
        class="toast"
        :class="`toast-${toast.kind}`"
        role="status"
        aria-live="polite"
      >{{ toast.text }}</div>
    </transition>
  </div>
</template>

<script>
import { VueDraggable } from 'vue-draggable-plus';
import MatrixRenderer from '@/components/quiz/MatrixRenderer.vue';
import LocaleSelector from '@/components/admin/LocaleSelector.vue';
import SectionPickerPopup from '@/components/admin/SectionPickerPopup.vue';
import RawJsonPanel from './RawJsonPanel.vue';
import apiService from '@/services/apiService';
import {
  questionsState,
  blankI18n,
  ensureAllLocales,
  localizeI18n,
  CODE_REGEX,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_LABELS,
} from '../_data/questions.js';

let uidCounter = 0;

const OPTION_ID_RE = /^opt_[1-9][0-9]*$/;
const ITEM_ID_RE = /^item_[1-9][0-9]*$/;
const MAX_ITEMS = 50;

function trimI18n(i18n) {
  // Drop empty entries so we don't ship `{ en: "", de: "", ... }` over
  // the wire for fields the author left blank. The renderer treats
  // missing locales as empty anyway.
  const out = {};
  if (i18n && typeof i18n === 'object') {
    for (const loc of SUPPORTED_LOCALES) {
      const v = i18n[loc];
      if (typeof v === 'string' && v.trim()) out[loc] = v.trim();
    }
  }
  return out;
}

function hasAnyLocaleFilled(i18n) {
  if (!i18n || typeof i18n !== 'object') return false;
  return SUPPORTED_LOCALES.some(loc => {
    const v = i18n[loc];
    return typeof v === 'string' && v.trim().length > 0;
  });
}

const FIELD_PLACEHOLDERS = {
  title: {
    en: 'How often per week do you usually eat these vegetables?',
    de: 'Wie oft pro Woche isst du normalerweise diese Gemüsesorten?',
    fr: '', it: '',
  },
  subtitle: {
    en: 'Vegetables', de: 'Gemüse', fr: 'Légumes', it: 'Verdure',
  },
  prompt: { en: '', de: '', fr: '', it: '' },
};

export default {
  name: 'MatrixEditor',
  components: { MatrixRenderer, LocaleSelector, VueDraggable, SectionPickerPopup, RawJsonPanel },
  data() {
    uidCounter += 1;
    return {
      uid: `mxe-${uidCounter}`,
      SUPPORTED_LOCALES,
      LOCALE_LABELS,
      placeholders: FIELD_PLACEHOLDERS,
      activeLang: DEFAULT_LOCALE,
      form: this.buildBlankForm(),
      initialSnapshot: null,
      // Monotonic counters: ids never reused even after delete, so old
      // analytics keys (opt_3 / item_5) never get re-bound to a new
      // option/item.
      nextOptionInt: 0,
      nextItemInt: 0,
      // Per-item flag for whether the tooltip textarea is expanded in
      // the editor. Independent of whether the tooltip has content.
      tooltipExpanded: {},
      touched: { code: false, title: false },
      serverError: null,
      loadError: null,
      loading: false,
      saving: false,
      answerCount: 0,
      rejectedCodes: [],
      showLeaveConfirm: false,
      showSectionPicker: false,
      pendingNext: null,
      cancelPending: false,
      showPasteDialog: false,
      pasteText: '',
      pasteLang: DEFAULT_LOCALE,
      // When non-null, a delete-confirm modal is open.
      // Shape: { kind: 'option'|'item', idx, title, preview }
      pendingDelete: null,
      toast: null,
      toastTimer: null,
    };
  },
  computed: {
    isEdit() {
      return this.$route.name === 'QuestionEditorEdit';
    },
    questionId() {
      return this.isEdit ? Number(this.$route.params.id) : null;
    },
    sections() {
      return questionsState.sections;
    },
    sectionPickLabel() {
      if (this.form.section_id == null) return 'Unassigned';
      const s = this.sections.find(x => x.id === this.form.section_id);
      if (!s) return `Section ${this.form.section_id}`;
      return this.sectionOptionLabel(s);
    },
    breadcrumbTail() {
      if (this.isEdit) {
        return this.initialSnapshot
          ? `Edit · ${this.initialSnapshot.code || 'question'}`
          : 'Edit';
      }
      return 'New matrix question';
    },
    pageTitle() {
      return this.isEdit ? 'Edit question' : 'New matrix question';
    },
    errors() {
      const e = {
        option_labels: {},
        option_shorts: {},
        item_labels: {},
      };

      const code = (this.form.code || '').trim();
      if (!code) {
        e.code = 'Code is required.';
      } else if (!CODE_REGEX.test(code)) {
        e.code = 'Code must be uppercase letters, digits or underscores. Max 32 chars.';
      } else if (this.rejectedCodes.includes(code)) {
        e.code = 'This code is already used by another question.';
      }

      if (!hasAnyLocaleFilled(this.form.content.title)) {
        e.title = 'Question title is required in at least one language.';
      }

      const opts = this.form.content.scale.options;
      if (!Array.isArray(opts) || opts.length < 2) {
        e.options_count = 'At least 2 scale options are required.';
      } else if (opts.length > 8) {
        e.options_count = 'No more than 8 scale options are allowed.';
      }
      for (const opt of (opts || [])) {
        if (!hasAnyLocaleFilled(opt.label)) {
          e.option_labels[opt.id] = 'Long label is required in at least one language.';
        }
        if (!hasAnyLocaleFilled(opt.label_short)) {
          // Soft warning, not a blocker. Runtime cards fall back to
          // the long `label` when the short one is empty, so the form
          // is saveable; we just nudge the author that chip layouts
          // (Vanilla B and similar) will look better with a short
          // form provided.
          e.option_shorts[opt.id] = 'Recommended for chip layouts. Falls back to the long label if empty.';
        }
      }

      const items = this.form.content.items;
      if (!Array.isArray(items) || items.length < 2) {
        e.items_count = 'At least 2 items are required.';
      } else if (items.length > MAX_ITEMS) {
        e.items_count = `No more than ${MAX_ITEMS} items are allowed.`;
      }
      for (const item of (items || [])) {
        if (!hasAnyLocaleFilled(item.label)) {
          e.item_labels[item.id] = 'Item label is required in at least one language.';
        }
      }

      return e;
    },
    isValid() {
      const e = this.errors;
      if (e.code || e.title || e.options_count || e.items_count) return false;
      if (Object.keys(e.option_labels).length > 0) return false;
      // option_shorts is intentionally NOT a save gate — it's a soft
      // warning surfaced inline. The runtime falls back to the long
      // label when label_short is empty, so the question still renders.
      if (Object.keys(e.item_labels).length > 0) return false;
      return true;
    },
    isDirty() {
      if (!this.initialSnapshot) return false;
      return JSON.stringify(this.serializeForm()) !== JSON.stringify(this.initialSnapshot);
    },
    /**
     * What the editor would save right now. Drives the live Raw JSON
     * panel — authors can watch the canonical save shape update as
     * they edit, and verify which fields will hit the DB. For legacy
     * questions migrated through this editor, the panel is also how
     * the team confirms the shape upgrade (e.g. content.scale[] →
     * content.scale.options[] with label_short fields present).
     */
    rawJsonView() {
      return this.serializeForm();
    },
    pasteLines() {
      return (this.pasteText || '')
        .split(/\r?\n/)
        .map(l => l.trim())
        .filter(l => l.length > 0);
    },
    pasteCount() {
      return this.pasteLines.length;
    },
    remainingSlots() {
      return Math.max(0, MAX_ITEMS - this.form.content.items.length);
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(id) {
        if (this.isEdit) this.loadExisting(id);
        else this.resetForNew();
      },
    },
  },
  created() {
    if (!this.isEdit) this.resetForNew();
  },
  mounted() {
    if (!questionsState.sections.length) this.fetchSections();
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    buildBlankForm() {
      // Per spec: 4-option scale with scores 0,1,2,3 (Likert default)
      // and 2 empty items. Gives the author a workable skeleton instead
      // of "before saving, build a scale and add 2 items".
      return {
        code: '',
        section_id: null,
        status: 'active',
        content: {
          title: blankI18n(),
          subtitle: blankI18n(),
          prompt: blankI18n(),
          scale: {
            options: [
              { id: 'opt_1', label: blankI18n(), label_short: blankI18n(), score: 0 },
              { id: 'opt_2', label: blankI18n(), label_short: blankI18n(), score: 1 },
              { id: 'opt_3', label: blankI18n(), label_short: blankI18n(), score: 2 },
              { id: 'opt_4', label: blankI18n(), label_short: blankI18n(), score: 3 },
            ],
          },
          items: [
            { id: 'item_1', label: blankI18n(), tooltip_body: blankI18n() },
            { id: 'item_2', label: blankI18n(), tooltip_body: blankI18n() },
          ],
        },
        help_text: blankI18n(),
      };
    },

    resetForNew() {
      this.form = this.buildBlankForm();
      this.nextOptionInt = 4;
      this.nextItemInt = 2;
      this.tooltipExpanded = {};
      this.initialSnapshot = this.serializeForm();
      this.answerCount = 0;
      this.loadError = null;
      this.serverError = null;
      this.rejectedCodes = [];
      this.touched = { code: false, title: false };
    },

    async loadExisting(id) {
      this.loadError = null;
      this.serverError = null;
      this.rejectedCodes = [];
      this.loading = true;
      try {
        const q = await apiService.fetchQuestion(id);
        if (q.question_type !== 'matrix') {
          this.loadError = `Question ${id} is not a matrix question.`;
          return;
        }
        // Tolerate two stored shapes for `content.scale`:
        //   { options: [...] }  ← current editor canonical
        //   [ ... ]             ← legacy / hand-authored rows from
        //                        before the wrapper was introduced
        // Legacy rows have no label_short — ensureAllLocales fills in
        // an empty i18n object so the author can populate it. Saving
        // re-writes the question in the canonical shape, migrating it
        // forward in place.
        let rawOptions = [];
        const scale = q.content?.scale;
        if (Array.isArray(scale?.options)) {
          rawOptions = scale.options;
        } else if (Array.isArray(scale)) {
          rawOptions = scale;
        }
        const options = rawOptions.map(opt => ({
          id: opt.id,
          label: ensureAllLocales(opt.label),
          label_short: ensureAllLocales(opt.label_short),
          score: Number.isInteger(opt.score) ? opt.score : 0,
        }));
        const rawItems = Array.isArray(q.content?.items) ? q.content.items : [];
        const items = rawItems.map(item => ({
          id: item.id,
          label: ensureAllLocales(item.label),
          tooltip_body: ensureAllLocales(item.tooltip_body),
        }));
        this.form = {
          code: q.code,
          section_id: q.section_id,
          status: q.status,
          content: {
            title: ensureAllLocales(q.content?.title),
            subtitle: ensureAllLocales(q.content?.subtitle),
            prompt: ensureAllLocales(q.content?.prompt),
            scale: { options },
            items,
          },
          help_text: ensureAllLocales(q.help_text),
        };
        // Track the highest opt_N and item_N ever assigned, so the
        // "+ Add" buttons never reuse an id that pointed at a now-deleted
        // option or item.
        this.nextOptionInt = options.reduce((m, opt) => {
          const match = OPTION_ID_RE.exec(opt.id || '');
          if (!match) return m;
          const n = parseInt(opt.id.slice(4), 10);
          return Number.isFinite(n) ? Math.max(m, n) : m;
        }, 0);
        this.nextItemInt = items.reduce((m, item) => {
          const match = ITEM_ID_RE.exec(item.id || '');
          if (!match) return m;
          const n = parseInt(item.id.slice(5), 10);
          return Number.isFinite(n) ? Math.max(m, n) : m;
        }, 0);
        this.tooltipExpanded = {};
        this.answerCount = q.answer_count || 0;
        this.initialSnapshot = this.serializeForm();
        this.touched = { code: false, title: false };
      } catch (e) {
        this.initialSnapshot = null;
        if (e?.response?.status === 404) {
          this.loadError = `No question found with id ${id}.`;
        } else {
          const detail = e?.response?.data?.detail;
          this.loadError = typeof detail === 'string'
            ? detail
            : 'Failed to load question from the server.';
        }
      } finally {
        this.loading = false;
      }
    },

    serializeForm() {
      const f = this.form;
      const options = (f.content.scale.options || []).map(opt => ({
        id: opt.id,
        label: trimI18n(opt.label),
        label_short: trimI18n(opt.label_short),
        score: Number.isInteger(opt.score) ? opt.score : 0,
      }));
      const items = (f.content.items || []).map(item => ({
        id: item.id,
        label: trimI18n(item.label),
        tooltip_body: trimI18n(item.tooltip_body),
      }));
      return {
        code: (f.code || '').trim(),
        section_id: f.section_id ?? null,
        status: f.status,
        content: {
          title: trimI18n(f.content.title),
          subtitle: trimI18n(f.content.subtitle),
          prompt: trimI18n(f.content.prompt),
          scale: { options },
          items,
        },
        help_text: trimI18n(f.help_text),
      };
    },

    onCodeInput(e) {
      this.form.code = (e.target.value || '').toUpperCase();
    },

    setOptionLabel(opt, locale, value) { opt.label[locale] = value; },
    setOptionShort(opt, locale, value) { opt.label_short[locale] = value; },
    setItemLabel(item, locale, value) { item.label[locale] = value; },
    setItemTooltip(item, locale, value) { item.tooltip_body[locale] = value; },

    addOption() {
      if (this.form.content.scale.options.length >= 8) return;
      this.nextOptionInt += 1;
      this.form.content.scale.options.push({
        id: `opt_${this.nextOptionInt}`,
        label: blankI18n(),
        label_short: blankI18n(),
        score: 0,
      });
    },

    deleteOption(idx) {
      if (this.form.content.scale.options.length <= 2) return;
      this.form.content.scale.options.splice(idx, 1);
    },

    requestDeleteOption(idx) {
      if (this.form.content.scale.options.length <= 2) return;
      const opt = this.form.content.scale.options[idx];
      if (!opt) return;
      // Preview = first non-empty label in the active locale, then any
      // other locale. Falls back to label_short if no long label is set.
      const preview =
        this.firstNonEmpty(opt.label, this.activeLang)
        || this.firstNonEmpty(opt.label_short, this.activeLang);
      this.pendingDelete = {
        kind: 'option',
        idx,
        title: 'Delete this option?',
        preview,
      };
    },

    requestDeleteItem(idx) {
      if (this.form.content.items.length <= 2) return;
      const item = this.form.content.items[idx];
      if (!item) return;
      const preview = this.firstNonEmpty(item.label, this.activeLang);
      this.pendingDelete = {
        kind: 'item',
        idx,
        title: 'Delete this item?',
        preview,
      };
    },

    confirmDelete() {
      const p = this.pendingDelete;
      if (!p) return;
      if (p.kind === 'option') this.deleteOption(p.idx);
      else if (p.kind === 'item') this.deleteItem(p.idx);
      this.pendingDelete = null;
    },

    cancelDelete() {
      this.pendingDelete = null;
    },

    firstNonEmpty(i18n, preferLocale) {
      if (!i18n || typeof i18n !== 'object') return '';
      const order = [preferLocale, ...SUPPORTED_LOCALES.filter(l => l !== preferLocale)];
      for (const loc of order) {
        const v = i18n[loc];
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
      return '';
    },

    addItem() {
      if (this.form.content.items.length >= MAX_ITEMS) return;
      this.nextItemInt += 1;
      this.form.content.items.push({
        id: `item_${this.nextItemInt}`,
        label: blankI18n(),
        tooltip_body: blankI18n(),
      });
    },

    deleteItem(idx) {
      if (this.form.content.items.length <= 2) return;
      const removed = this.form.content.items[idx];
      this.form.content.items.splice(idx, 1);
      if (removed && this.tooltipExpanded[removed.id]) {
        const next = { ...this.tooltipExpanded };
        delete next[removed.id];
        this.tooltipExpanded = next;
      }
    },

    toggleTooltipEditor(itemId) {
      this.tooltipExpanded = {
        ...this.tooltipExpanded,
        [itemId]: !this.tooltipExpanded[itemId],
      };
    },

    itemHasAnyTooltip(item) {
      return hasAnyLocaleFilled(item.tooltip_body);
    },

    openPasteDialog() {
      if (this.form.content.items.length >= MAX_ITEMS) return;
      this.pasteText = '';
      this.pasteLang = this.activeLang;
      this.showPasteDialog = true;
    },

    closePasteDialog() {
      this.showPasteDialog = false;
      this.pasteText = '';
    },

    onConfirmPaste() {
      const lines = this.pasteLines.slice(0, this.remainingSlots);
      for (const line of lines) {
        if (this.form.content.items.length >= MAX_ITEMS) break;
        this.nextItemInt += 1;
        const label = blankI18n();
        label[this.pasteLang] = line;
        this.form.content.items.push({
          id: `item_${this.nextItemInt}`,
          label,
          tooltip_body: blankI18n(),
        });
      }
      const added = lines.length;
      this.closePasteDialog();
      if (added > 0) {
        this.showToast(
          'success',
          `Added ${added} ${added === 1 ? 'item' : 'items'}.`,
        );
      }
    },

    isLocaleFilled(loc) {
      const c = this.form.content;
      const i18nFields = [c.title, c.subtitle, c.prompt, this.form.help_text];
      for (const f of i18nFields) {
        if (f && typeof f[loc] === 'string' && f[loc].trim()) return true;
      }
      for (const opt of (c.scale.options || [])) {
        if (opt.label && typeof opt.label[loc] === 'string' && opt.label[loc].trim()) return true;
        if (opt.label_short && typeof opt.label_short[loc] === 'string' && opt.label_short[loc].trim()) return true;
      }
      for (const item of (c.items || [])) {
        if (item.label && typeof item.label[loc] === 'string' && item.label[loc].trim()) return true;
        if (item.tooltip_body && typeof item.tooltip_body[loc] === 'string' && item.tooltip_body[loc].trim()) return true;
      }
      return false;
    },

    sectionOptionLabel(section) {
      const name = localizeI18n(section.name, 'en');
      const collisions = this.sections.filter(
        s => localizeI18n(s.name, 'en') === name,
      ).length;
      if (collisions > 1 && section.questionnaire_name) {
        return `${name} (${section.questionnaire_name})`;
      }
      return name || section.slug || `Section ${section.id}`;
    },

    async fetchSections() {
      try {
        const sections = await apiService.fetchSections();
        questionsState.sections.splice(
          0,
          questionsState.sections.length,
          ...sections,
        );
      } catch (e) {
        console.warn('Could not load sections:', e);
      }
    },

    onSectionPicked(section) {
      this.form.section_id = section.id;
      const idx = questionsState.sections.findIndex(s => s.id === section.id);
      if (idx === -1) {
        questionsState.sections.push(section);
      } else {
        questionsState.sections.splice(idx, 1, section);
      }
      this.showSectionPicker = false;
    },

    async onSave() {
      this.touched.code = true;
      this.touched.title = true;
      if (!this.isValid || this.saving) return;

      this.serverError = null;
      this.saving = true;
      const payload = this.serializeForm();

      try {
        if (this.isEdit) {
          const updated = await apiService.updateQuestion(this.questionId, {
            code: payload.code,
            section_id: payload.section_id,
            status: payload.status,
            content: payload.content,
            help_text: payload.help_text,
          });
          this._upsertCached(updated);
          this.initialSnapshot = this.serializeForm();
          this.showToast('success', 'Question saved.');
          this.$router.push('/admin/questions');
        } else {
          const created = await apiService.createQuestion({
            code: payload.code,
            section_id: payload.section_id,
            question_type: 'matrix',
            content: payload.content,
            help_text: payload.help_text,
            status: payload.status,
          });
          this._upsertCached(created);
          this.initialSnapshot = this.serializeForm();
          this.showToast('success', `Created question ${created.code}.`);
          this.$router.push('/admin/questions');
        }
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 409) {
          const code = (this.form.code || '').trim();
          if (code && !this.rejectedCodes.includes(code)) {
            this.rejectedCodes.push(code);
          }
          this.serverError = typeof detail === 'string'
            ? detail
            : 'A question with that code already exists.';
        } else if (status === 422) {
          this.serverError = Array.isArray(detail)
            ? detail.map(d => d.msg).join(' ')
            : (typeof detail === 'string' ? detail : 'Validation failed.');
        } else if (status === 404) {
          this.serverError = 'The question no longer exists. It may have been deleted.';
        } else {
          this.serverError = typeof detail === 'string'
            ? detail
            : 'Could not save question. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    },

    _upsertCached(item) {
      if (!item || item.id == null) return;
      const items = questionsState.items;
      const idx = items.findIndex(q => q.id === item.id);
      if (idx === -1) items.push(item);
      else items.splice(idx, 1, item);
    },

    onCancel() {
      if (this.isDirty) {
        this.cancelPending = true;
        this.showLeaveConfirm = true;
        return;
      }
      this.$router.push('/admin/questions');
    },

    onCancelLeave() {
      this.showLeaveConfirm = false;
      if (this.pendingNext) {
        this.pendingNext(false);
        this.pendingNext = null;
      }
      this.cancelPending = false;
    },

    onConfirmLeave() {
      this.showLeaveConfirm = false;
      if (this.cancelPending) {
        this.cancelPending = false;
        this.initialSnapshot = this.serializeForm();
        this.$router.push('/admin/questions');
        return;
      }
      if (this.pendingNext) {
        const next = this.pendingNext;
        this.pendingNext = null;
        this.initialSnapshot = this.serializeForm();
        next();
      }
    },

    showToast(kind, text) {
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toast = { kind, text };
      const ms = kind === 'error' ? 6000 : 3500;
      this.toastTimer = setTimeout(() => {
        this.toast = null;
        this.toastTimer = null;
      }, ms);
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty && !this.cancelPending) {
      this.pendingNext = next;
      this.showLeaveConfirm = true;
    } else {
      next();
    }
  },
};
</script>

<style scoped>
.matrix-editor-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad: #C8392E;
  --color-bad-dark: #A92F26;
  --color-bad-soft: rgba(200, 57, 46, 0.10);
  --color-warn: #E8A33D;
  --color-warn-soft: rgba(232, 163, 61, 0.18);
  --color-warn-dark: #6B4A0F;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.matrix-editor-wrap *,
.matrix-editor-wrap *::before,
.matrix-editor-wrap *::after {
  box-sizing: border-box;
}

.page {
  max-width: 1080px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
}

.breadcrumb {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.breadcrumb-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  border-bottom: 1px solid transparent;
}

.breadcrumb-link:hover {
  border-bottom-color: var(--color-text-secondary);
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.page-header-text {
  flex: 1;
  min-width: 240px;
}

.page-header-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-top: 4px;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  margin: 0 0 0.5rem;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 16px;
  color: var(--color-text-secondary);
  margin: 0;
  font-weight: 400;
}

.btn {
  padding: 9px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s, color 0.15s;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}

.btn-danger {
  background: var(--color-bad);
  color: white;
  border-color: var(--color-bad);
}

.btn-danger:hover {
  background: var(--color-bad-dark);
  border-color: var(--color-bad-dark);
}

.server-error {
  font-size: 13px;
  color: #6B1F18;
  background: var(--color-bad-soft);
  border: 0.5px solid rgba(200, 57, 46, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.answer-warning {
  font-size: 13px;
  color: var(--color-warn-dark);
  background: var(--color-warn-soft);
  border: 0.5px solid rgba(232, 163, 61, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1.5rem;
  line-height: 1.5;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.warning-icon {
  font-size: 14px;
  line-height: 1.4;
}

.load-error {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-text-secondary);
}

.loading-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-text-tertiary);
  font-size: 14px;
}

.back-link {
  color: var(--color-accent);
  text-decoration: none;
  font-size: 14px;
}

.back-link:hover {
  text-decoration: underline;
}

.editor-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 32px;
  align-items: start;
}

@media (max-width: 900px) {
  .editor-grid { grid-template-columns: 1fr; }
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
}

.preview-column {
  position: sticky;
  top: 1.5rem;
}

.preview-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-tertiary);
  font-weight: 600;
  margin-bottom: 8px;
}

.preview-frame {
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04);
}

.preview-note {
  margin: 10px 0 0;
  font-size: 11.5px;
  color: var(--color-text-tertiary);
  font-style: italic;
  line-height: 1.4;
}

.fieldset {
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1.25rem 1.5rem 1.5rem;
  margin: 0;
  background: var(--color-bg-card);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.fieldset-legend {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-tertiary);
  padding: 0 6px;
  margin-left: -6px;
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
}

.fieldset-note {
  margin: 0;
  font-size: 12px;
  color: var(--color-text-secondary);
  background: var(--color-bg-soft);
  padding: 8px 10px;
  border-radius: var(--radius-md);
  border-left: 3px solid var(--color-accent);
}

.option-count {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  text-transform: none;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}

.tooltip-label {
  margin-top: 4px;
}

.lang-tag {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-accent);
  background: var(--color-accent-soft);
  padding: 1px 6px;
  border-radius: 3px;
}

.optional {
  color: var(--color-text-tertiary);
  font-weight: 400;
  font-size: 12px;
}

.field-input {
  width: 100%;
  padding: 10px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  font-size: 14px;
  line-height: 1.4;
}

.field-input.mono {
  font-family: var(--font-mono);
  font-size: 13px;
  letter-spacing: 0.02em;
}

.field-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-soft);
}

.field-input.has-error {
  border-color: var(--color-bad);
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.15);
}

.field-input.has-error:focus {
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.25);
}

/* Soft-warning variant — used for non-blocking nudges like the
   missing `label_short` indicator. Amber rather than red so it's
   visually distinct from a save-blocking error. */
.field-input.has-warning {
  border-color: #C6890A;
  box-shadow: 0 0 0 2px rgba(198, 137, 10, 0.12);
}

.field-input.has-warning:focus {
  box-shadow: 0 0 0 2px rgba(198, 137, 10, 0.22);
}

.field-textarea {
  resize: vertical;
  min-height: 60px;
  font-family: var(--font-sans);
}

.field-select { appearance: auto; }

/* Section picker button (replaces the legacy <select>). Same visual
   contract across all three question editors so the field reads as
   one thing whether you're in stepper / single_choice / matrix mode. */
.section-pick-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  font-family: inherit;
  font-size: 14px;
  color: var(--color-text-primary);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.section-pick-btn:hover {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.section-pick-btn.is-unassigned .section-pick-label {
  color: var(--color-text-tertiary);
  font-style: italic;
}

.section-pick-action {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent);
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.section-clear-btn {
  align-self: flex-start;
  margin-top: 4px;
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 0;
  text-decoration: underline;
}

.section-clear-btn:hover {
  color: var(--color-text-secondary);
}

.field-hint {
  font-size: 11.5px;
  color: var(--color-text-tertiary);
  margin: 0;
  line-height: 1.4;
}

.field-hint.dim {
  font-style: italic;
  color: var(--color-warn-dark);
}

.field-hint.tiny {
  font-size: 10.5px;
  margin-top: 2px;
}

.field-error {
  font-size: 12px;
  color: var(--color-bad);
  margin: 0;
}

/* Soft-warning message — paired with .has-warning on the input. */
.field-warning {
  font-size: 12px;
  color: #8a5f06;
  margin: 0;
}

.fieldset-error {
  margin-top: -4px;
  margin-bottom: 4px;
}

.radio-row {
  display: flex;
  gap: 18px;
  padding: 4px 0;
}

.radio-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--color-text-primary);
  cursor: pointer;
}

.radio-option input {
  accent-color: var(--color-accent);
  cursor: pointer;
}

/* ───────── Scale options list ───────── */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-row {
  display: grid;
  grid-template-columns: 22px 28px 1fr 90px 28px;
  align-items: start;
  gap: 8px;
  padding: 8px 4px;
  border-radius: var(--radius-md);
  transition: background 0.15s;
}

.option-row:hover { background: var(--color-bg-soft); }

.option-fields {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 8px;
  min-width: 0;
}

.option-label-cell,
.option-short-cell,
.option-score-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.option-label-input,
.option-short-input {
  font-size: 13px;
  padding: 8px 10px;
}

.option-label-error { font-size: 11px; }

.drag-handle {
  font-size: 14px;
  color: var(--color-text-tertiary);
  cursor: grab;
  user-select: none;
  padding-top: 12px;
  text-align: center;
  letter-spacing: -2px;
}

.drag-handle:active { cursor: grabbing; }

.option-number {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  padding-top: 12px;
  text-align: center;
}

.number-input {
  font-family: var(--font-mono);
  font-size: 13px;
  text-align: center;
  padding: 8px 6px;
}

.option-score-input { width: 100%; }

.option-delete {
  width: 28px;
  height: 32px;
  margin-top: 2px;
  background: transparent;
  border: 0.5px solid transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-tertiary);
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.option-delete:hover:not(:disabled) {
  background: var(--color-bad-soft);
  color: var(--color-bad);
  border-color: var(--color-bad-soft);
}

.option-delete:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.score-hint { margin-top: -2px; }

.add-option-btn {
  align-self: flex-start;
  margin-top: 4px;
}

/* ───────── Items list ───────── */
.items-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-row-wrap {
  border-radius: var(--radius-md);
  transition: background 0.15s;
}

.item-row-wrap:hover { background: var(--color-bg-soft); }

.item-row {
  display: grid;
  grid-template-columns: 22px 28px 1fr 32px 28px;
  align-items: start;
  gap: 8px;
  padding: 6px 4px;
}

.item-label-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.item-label-input {
  font-size: 13px;
  padding: 8px 10px;
}

.tooltip-toggle {
  width: 28px;
  height: 32px;
  margin-top: 2px;
  background: transparent;
  border: 0.5px solid transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-tertiary);
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.tooltip-toggle:hover {
  background: var(--color-accent-soft);
  color: var(--color-accent);
}

.tooltip-toggle.has-content {
  color: var(--color-accent);
}

.tooltip-toggle.is-open {
  background: var(--color-accent-soft);
  color: var(--color-accent);
  border-color: var(--color-accent);
}

.item-tooltip-editor {
  padding: 8px 1rem 12px 4rem;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.items-toolbar {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}

/* ───────── Modal + toast ───────── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2000;
}

.modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-paste {
  max-width: 560px;
}

.modal-paste .field { margin-bottom: 1rem; }

.modal-delete {
  max-width: 380px;
  text-align: center;
}

.modal-delete .modal-footer { justify-content: center; }

.modal-icon {
  width: 44px;
  height: 44px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: var(--color-bad-soft);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-icon-glyph {
  font-size: 26px;
  font-weight: 600;
  line-height: 1;
  color: var(--color-bad);
}

.modal-quote {
  display: inline-block;
  font-weight: 500;
  color: var(--color-text-primary);
  background: var(--color-bg-soft);
  border-radius: 4px;
  padding: 1px 6px;
  margin: 0 2px;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px;
}

.modal-text {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.toast {
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  background: var(--color-text-primary);
  color: white;
  padding: 11px 18px;
  border-radius: var(--radius-md);
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  max-width: min(90vw, 520px);
  z-index: 3000;
}

.toast-success { background: #2E7D32; }
.toast-error { background: var(--color-bad-dark); }

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
</style>
